/* eslint-disable @typescript-eslint/member-delimiter-style */
/* eslint-disable @typescript-eslint/naming-convention */
import { Observable, of, throwError } from 'rxjs';
import { catchError, delay, map, retry, switchMap, tap, timeout } from 'rxjs/operators';
import { Inject, Injectable, Optional, PLATFORM_ID } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';

import { Token } from '~core/modules/identity/models/identity.model';
import { IdentityService } from '~core/modules/identity/services/identity.service';
import { ApiConfig } from '~shared/constants/api.config';
import { GeneralHttpError, HttpErrorStatuses } from '~shared/modules/error-handler';
import { EXPONEA_COOKIE, FeatureToggleEnum } from '~shared/constants';

import { authorizationEndpoints, FF_SL_ENABLE_PERSON_SIGNUP_V2, IMS_ROLE } from '../constants/authorization.constant';
import { DigitalAccountError } from '../classes/digital-account-error.class';
import { MetroCardholderCredentials } from '../models/metro-cardholder-credentials.model';
import { Credentials } from '../models/credentials.model';
import { ApplicationEventService } from '~shared/modules/application-event/services/application-event.service';
// prettier-ignore
import {
  AuthorizationApplicationEvent
} from '~shared/modules/application-event/models/authorization-application-event.model';
import { UserTypeUtil } from '~shared/utils/user-type.util';
import { IAccountInformation } from '~shared/models';
import {
  ApplicationAuthorizationActionType as AuthType,
  AutologinAuthorizationLabel,
} from '~shared/modules/application-event/constants';
import { getCookie } from '~shared/utils/cookie.util';
import { ServerLoggerService } from '~shared/modules/server-logger/services/server-logger.service';
import { isPlatformBrowser } from '@angular/common';
import { QueryParamsEncoder } from '~shared/helpers/query-encoder';
import type { Customer } from '../components/customers-list/customers-list.component';
import { UrlUtil } from '~shared/utils/url.util';
import { getCountryDomainCode } from '~server/utils/country-detection.util';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';

const LOG_DELAY = 4000;
const CONTENT_TYPE = 'application/x-www-form-urlencoded';

export interface IResetPasswordPayload {
  email: string;
  userType: string;
  successUrl: string;
  expirationUrl: string;
  realmId: string;
  clientId: string;
  countryCode: string;
  localeId: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthorizationHttpService {
  constructor(
    private httpClient: HttpClient,
    private identityService: IdentityService,
    private applicationEventService: ApplicationEventService,
    // eslint-disable-next-line @typescript-eslint/ban-types
    @Inject(PLATFORM_ID) private platformId: Object,
    private serverLoggerService: ServerLoggerService,
    private queryParamsEncoder: QueryParamsEncoder,
    @Optional() @Inject(REQUEST) private webAppBuyerRequest: any,
    private activeFeaturesConfigCatService: ActiveFeaturesConfigcatService
  ) {}

  public login(username: string, password: string): Observable<any> {
    const exponeaCookie = getCookie(EXPONEA_COOKIE);
    const body = {
      username,
      password,
      role: IMS_ROLE,
      ...(exponeaCookie && { exponeaCookie }),
    };

    return this.httpClient.post(`${ApiConfig.SVC_USER_ACCOUNT_API_URL()}/auth/login`, body).pipe(
      switchMap((token: Token) =>
        this.identityService.authorize(token).pipe(
          tap((userInfo: IAccountInformation) => {
            this.trackAuth(AuthType.LoggedIn, userInfo.registrationType);
          }),
          catchError((error) => {
            const isFailedDependencyError = error.status === HttpErrorStatuses.FailedDependency;
            if (isFailedDependencyError) {
              return throwError(new DigitalAccountError(token));
            }

            return throwError(error);
          })
        )
      )
    );
  }

  public registerDigitalAccount(token: Token): Observable<any> {
    return this.httpClient
      .post(authorizationEndpoints().digitalAccount, { token: token.access_token })
      .pipe(
        switchMap(() =>
          this.identityService
            .authorize(token)
            .pipe(
              tap((userData: IAccountInformation) => this.trackAuth(AuthType.Registered, userData.registrationType))
            )
        )
      );
  }

  public registerNewEntrepreneur(credentials: Credentials): Observable<any> {
    return this.register(authorizationEndpoints().newEntrepreneur, credentials);
  }

  public registerConsumer(credentials: Credentials): Observable<any> {
    return this.register(authorizationEndpoints().consumer, credentials);
  }

  public registerMetroCardholder(credentials: MetroCardholderCredentials): Observable<any> {
    return this.register(authorizationEndpoints().metroCardHolder, credentials);
  }

  public validateEmail(email): Observable<any> {
    const body = new HttpParams({
      fromObject: {
        email,
      },
      encoder: this.queryParamsEncoder,
    });

    return this.httpClient.post(authorizationEndpoints().validateEmail, body, {
      headers: {
        'Content-Type': CONTENT_TYPE,
      },
    });
  }

  public searchCustomers(rawParams: { query: string; filterMetroEmployees: string; limit?: string }) {
    const params = UrlUtil.encodeQueryParams(rawParams);

    return this.httpClient.get<{ count: number; items: Customer[] }>(
      `${ApiConfig.SVC_BUYER_ACCOUNT_EMPLOYEE_SEARCH_CUSTOMERS_API_URL()}`,
      { params }
    );
  }

  public impersonationLogout() {
    return this.httpClient.delete(`${ApiConfig.SVC_BUYER_ACCOUNT_EMPLOYEE_IMPERSONATE_API_URL()}`);
  }

  public getCompanyDetails(vatId) {
    const countryCode = getCountryDomainCode(this.webAppBuyerRequest);

    const url = ApiConfig.SVC_BUYER_ACCOUNT_SELECTED_BRANCH_DATA_FROM_RSIN_ENDPOINT_V2(vatId);

    return this.httpClient.get(url, {
      headers: new HttpHeaders({
        'Country-Code': countryCode,
      }),
    });
  }

  public IDAMRegister(email, password, passwordless: boolean): Observable<any> {
    const payload = {
      email,
      agreedToTermsAndConditions: true,
      readDataProtectionNotice: true,
    };
    if (passwordless) {
      Object.assign(payload, { passwordless });
    } else {
      Object.assign(payload, { password });
    }

    return this.activeFeaturesConfigCatService
      .isFeatureEnabledReadyForCurrentCountry$(FF_SL_ENABLE_PERSON_SIGNUP_V2)
      .pipe(
        switchMap((isEnabled) =>
          this.httpClient.post(authorizationEndpoints()[isEnabled ? 'idamRegisterV2' : 'idamRegister'], payload)
        )
      );
  }

  public idamProcessSignedUp(issuer, body, autoLoginRedirectURI, autoLoginRedirect): Observable<any> {
    return this.defaultAutoLogin(issuer, body, autoLoginRedirectURI, autoLoginRedirect);
  }
  public sendMagicLinkV2(email: string, redirectionUrl: string, countryCode: string) {
    const body = {
      email : email,
      redirectionUrl : redirectionUrl,
    }
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'country-code': countryCode,
    });
    return this.httpClient.post<{}>(
      ApiConfig.SVC_BUYER_ACCOUNT_SEND_MAGIC_LINK_API_V2_URL(),
      body,
      { headers },
    );
  }

  public sendResetPassword(payload: Required<IResetPasswordPayload>) {
    const formDataPayload = new HttpParams({
      fromObject: payload,
      encoder: this.queryParamsEncoder,
    });

    return this.httpClient.post<{
      status: number;
      message: string;
      results: any[];
      links: string[];
    }>(ApiConfig.BUYER_ACCOUNT_SEND_RESET_PASSWORD_API_URL(), formDataPayload);
  }

  private tryAutoLogin(params: HttpParams, issuer: string, autoLoginRedirectURI: string) {
    return of(null).pipe(
      delay(LOG_DELAY),
      map(() => {
        params = params.set('redirect_uri', autoLoginRedirectURI);
        const fields = {};
        const form = document.createElement('form');

        form.method = 'POST';
        form.action = `${issuer}/authorize/api/oauth2/processSignedUp`;

        params.keys().forEach((key) => {
          fields[key] = document.createElement('input');
          fields[key].value = params.get(key);
          fields[key].name = key;
          form.appendChild(fields[key]);
        });

        document.body.appendChild(form);

        form.submit();
        return null;
      })
    );
  }

  private defaultAutoLogin(issuer, body, autoLoginRedirectURI, autoLoginRedirect) {
    const request$ = this.httpClient.post(`${issuer}/authorize/api/oauth2/processSignedUp`, body, {
      headers: {
        'Content-Type': CONTENT_TYPE,
        Accept: 'text/html',
      },
      withCredentials: true,
      responseType: 'text',
    });

    return request$.pipe(
      tap(() => {
        this.applicationEventService.publishIfFFEnabled(
          FeatureToggleEnum.FF_TRACK_IDAM_AUTOLOGIN,
          new AuthorizationApplicationEvent(AuthType.AutologinRequest, AutologinAuthorizationLabel.Ok)
        );
        if (isPlatformBrowser(this.platformId)) {
          this.serverLoggerService.info('TIER1 SIGNUP: Auto-login after Tier1 signup succeeded');
        }
      }),
      catchError((error: GeneralHttpError) => {
        // eslint-disable-next-line no-magic-numbers
        const isCORS = error?.sourceError?.status === 0;
        const parsedError = error?.sourceError || {};

        this.applicationEventService.publishIfFFEnabled(
          FeatureToggleEnum.FF_TRACK_IDAM_AUTOLOGIN,
          new AuthorizationApplicationEvent(AuthType.AutologinRequest, AutologinAuthorizationLabel.Fail)
        );

        if (isPlatformBrowser(this.platformId)) {
          this.serverLoggerService.error(
            `TIER1 SIGNUP: Auto-login after Tier1 signup failed${isCORS ? ' (CORS)' : ''}`,
            {
              ...parsedError,
              correlationId: autoLoginRedirect,
            }
          );
        }
        return this.tryAutoLogin(body, issuer, autoLoginRedirectURI);
      })
    );
  }

  private register(url: string, credentials: any): Observable<any> {
    const exponeaCookie = getCookie(EXPONEA_COOKIE);

    return this.httpClient
      .post(url, {
        ...credentials,
        ...(exponeaCookie && { exponeaCookie }),
      })
      .pipe(
        switchMap((token: Token) =>
          this.identityService
            .authorize(token)
            .pipe(
              tap((userData: IAccountInformation) =>
                this.trackAuth(AuthType.Registered, userData.registrationType, credentials.isHoreca)
              )
            )
        ),
        catchError((e) => this.handleAuthErrors(AuthType.RegistrationFailed, e))
      );
  }

  private trackAuth(event, userType, isHoreca?) {
    this.applicationEventService.publish(
      new AuthorizationApplicationEvent(event, UserTypeUtil.getUserType(userType), isHoreca)
    );
  }

  private handleAuthErrors(event, error) {
    const errorLabel = error.invalidParams
      ? Object.values(error.invalidParams)
          .map((e: any) => e.code)
          .join(', ')
      : error.detail;
    this.applicationEventService.publish(new AuthorizationApplicationEvent(event, errorLabel), true);
    return throwError(error);
  }

  public getLoginLink(redirectUrl) {
    return this.httpClient
      .get(`${ApiConfig.SVC_BUYER_API_URL()}/login-link?redirectUrl=${encodeURIComponent(redirectUrl)}`)
      .pipe(
        timeout(3000),
        retry(1),
        catchError((error) => {
          if (isPlatformBrowser(this.platformId)) {
            this.serverLoggerService.error('BuyerAccount login-link failed', {
              ...error,
              correlationId: error?.sourceError?.headers?.get('x-correlation-id'),
            });
          }
          return throwError(error);
        })
      );
  }
}
